import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Message } from 'primereact/message';
import { equipmentAPI } from '../services/api';
import { Equipment, EquipmentStatus } from '../types';

const statusOptions: { label: string; value: EquipmentStatus }[] = [
  { label: 'Aktivan', value: 'active' },
  { label: 'Neaktivan', value: 'inactive' },
  { label: 'Održavanje', value: 'maintenance' },
  { label: 'Otpisan', value: 'retired' },
  { label: 'Pozajmljen', value: 'borrowed' }
];

const statusSeverity: Record<EquipmentStatus, 'success' | 'info' | 'warning' | 'danger' | 'secondary'> = {
  active: 'success',
  inactive: 'secondary',
  maintenance: 'warning',
  retired: 'info',
  borrowed: 'danger'
};

const ITEquipment: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [items, setItems] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState('');

  const [editVisible, setEditVisible] = useState(false);
  const [editing, setEditing] = useState<Equipment | null>(null);
  const [form, setForm] = useState({
    name: '',
    type: '',
    brand: '',
    deviceModel: '',
    serialNumber: '',
    location: '',
    status: 'active' as EquipmentStatus,
    quantity: 1,
    ipAddress: '',
    macAddress: '',
    description: '',
    category: ''
  });

  const resetForm = () => {
    setEditing(null);
    setForm({
      name: '',
      type: '',
      brand: '',
      deviceModel: '',
      serialNumber: '',
      location: '',
      status: 'active',
      quantity: 1,
      ipAddress: '',
      macAddress: '',
      description: '',
      category: ''
    });
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await equipmentAPI.getAll();
      setItems(data);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError(e?.response?.data?.error || 'Greška pri učitavanju opreme');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openNew = () => {
    resetForm();
    setEditVisible(true);
  };

  const openEdit = (row: Equipment) => {
    setEditing(row);
    setForm({
      name: row.name || '',
      type: row.type || '',
      brand: row.brand || '',
      deviceModel: row.deviceModel || '',
      serialNumber: row.serialNumber || '',
      location: row.location || '',
      status: row.status || 'active',
      quantity: row.quantity ?? 1,
      ipAddress: row.ipAddress || '',
      macAddress: row.macAddress || '',
      description: row.description || '',
      category: row.category || ''
    });
    setEditVisible(true);
  };

  const confirmDelete = (row: Equipment) => {
    confirmDialog({
      header: 'Potvrda brisanja',
      message: `Obrisati opremu "${row.name}"?`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await equipmentAPI.delete(row._id);
          toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Oprema obrisana', life: 2500 });
          await load();
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.error || 'Greška pri brisanju opreme',
            life: 5000
          });
        }
      }
    });
  };

  const canSubmit = useMemo(() => {
    return (
      form.name.trim() &&
      form.type.trim() &&
      form.brand.trim() &&
      form.deviceModel.trim() &&
      form.serialNumber.trim() &&
      form.location.trim() &&
      form.quantity > 0
    );
  }, [form]);

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validacija',
        detail: 'Popunite sva obavezna polja označena zvezdicom',
        life: 3000
      });
      return;
    }

    setLoading(true);
    try {
      if (editing) {
        await equipmentAPI.update(editing._id, {
          ...form
        });
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Oprema ažurirana', life: 2500 });
      } else {
        await equipmentAPI.create(form);
        toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Oprema kreirana', life: 2500 });
      }
      setEditVisible(false);
      await load();
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri čuvanju opreme',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const statusBody = (row: Equipment) => (
    <Tag value={statusOptions.find((s) => s.value === row.status)?.label || row.status} severity={statusSeverity[row.status]} />
  );

  const quantityBody = (row: Equipment) => {
    const total = row.quantity ?? 1;
    const assigned =
      row.assignments?.reduce((sum, a) => {
        return sum + (a.quantity || 0);
      }, 0) || 0;
    const available = Math.max(total - assigned, 0);
    return (
      <span>
        {total} / <strong>{available}</strong>
      </span>
    );
  };

  const portsBody = (row: Equipment) => {
    const count = row.ports?.length || 0;
    return <span>{count > 0 ? `${count} portova` : '-'}</span>;
  };

  const actionsBody = (row: Equipment) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-pencil"
        className="p-button-sm p-button-rounded p-button-text"
        onClick={() => openEdit(row)}
        tooltip="Uredi"
      />
      <Button
        icon="pi pi-users"
        className="p-button-sm p-button-rounded p-button-text"
        disabled
        tooltip="Dodela korisnicima (uskoro)"
      />
      <Button
        icon="pi pi-trash"
        className="p-button-sm p-button-rounded p-button-text p-button-danger"
        onClick={() => confirmDelete(row)}
        tooltip="Obriši"
      />
    </div>
  );

  return (
    <div className="p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">Upravljanje opremom</h2>
          <div className="text-600 mt-1">Pregled i unos IT opreme</div>
        </div>
        <div className="flex gap-2">
          <Button label="Dodaj opremu" icon="pi pi-plus" onClick={openNew} />
          <Button label="Osveži" icon="pi pi-refresh" severity="secondary" onClick={load} />
        </div>
      </div>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <Card>
        <div className="flex justify-content-end mb-3">
          <span className="p-input-icon-left" style={{ width: 'min(380px, 100%)' }}>
            <i className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Pretraga..."
              style={{ width: '100%' }}
            />
          </span>
        </div>

        <DataTable
          value={items}
          loading={loading}
          paginator
          rows={10}
          emptyMessage="Nema opreme"
          className="p-datatable-sm"
          globalFilter={globalFilter}
          globalFilterFields={['name', 'type', 'brand', 'deviceModel', 'location', 'serialNumber', 'ipAddress', 'macAddress']}
        >
          <Column field="name" header="Naziv" sortable />
          <Column field="type" header="Tip" sortable />
          <Column field="brand" header="Brend" sortable />
          <Column field="deviceModel" header="Model" sortable />
          <Column field="location" header="Lokacija" sortable />
          <Column header="Količina / Dostupno" body={quantityBody} sortable sortField="quantity" style={{ width: '170px' }} />
          <Column field="status" header="Status" body={statusBody} sortable style={{ width: '140px' }} />
          <Column header="Portovi" body={portsBody} style={{ width: '130px' }} />
          <Column header="Akcije" body={actionsBody} style={{ width: '170px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={editing ? 'Izmeni opremu' : 'Dodaj opremu'}
        visible={editVisible}
        onHide={() => setEditVisible(false)}
        style={{ width: '800px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        modal
        draggable={false}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Otkaži"
              className="p-button-secondary p-button-outlined"
              onClick={() => {
                setEditVisible(false);
              }}
            />
            <Button label={editing ? 'Sačuvaj' : 'Kreiraj'} icon="pi pi-check" onClick={handleSubmit} loading={loading} disabled={!canSubmit} />
          </div>
        }
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-6">
            <label>Naziv *</label>
            <InputText value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Tip *</label>
            <InputText value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Brend *</label>
            <InputText value={form.brand} onChange={(e) => setForm((p) => ({ ...p, brand: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Model *</label>
            <InputText value={form.deviceModel} onChange={(e) => setForm((p) => ({ ...p, deviceModel: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Serijski broj *</label>
            <InputText value={form.serialNumber} onChange={(e) => setForm((p) => ({ ...p, serialNumber: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Lokacija *</label>
            <InputText value={form.location} onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-4">
            <label>Status *</label>
            <Dropdown
              value={form.status}
              options={statusOptions}
              onChange={(e) => setForm((p) => ({ ...p, status: e.value }))}
              optionLabel="label"
              optionValue="value"
              appendTo="self"
            />
          </div>
          <div className="field col-12 md:col-4">
            <label>Količina *</label>
            <InputNumber
              value={form.quantity}
              onValueChange={(e) => setForm((p) => ({ ...p, quantity: e.value ?? 0 }))}
              min={1}
              showButtons
            />
          </div>
          <div className="field col-12 md:col-4">
            <label>Kategorija</label>
            <InputText value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>IP adresa</label>
            <InputText value={form.ipAddress} onChange={(e) => setForm((p) => ({ ...p, ipAddress: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>MAC adresa</label>
            <InputText value={form.macAddress} onChange={(e) => setForm((p) => ({ ...p, macAddress: e.target.value }))} />
          </div>
          <div className="field col-12">
            <label>Opis</label>
            <InputTextarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              rows={4}
              autoResize
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ITEquipment;






